import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { Inbox, Scissors, Package, Truck, CheckCheck } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { format, parseISO } from 'date-fns';
import { getPipelineOrders } from '@/api/orders';
import PipelineOrderRow from '@/components/PipelineOrderRow';
import { formatCurrency } from '@/utils/orderValue';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';

const STAGES = [
  { key: 'accepted',  label: 'Accepted',  Icon: Inbox },
  { key: 'in_design', label: 'In Design', Icon: Scissors },
  { key: 'dispatch',  label: 'Dispatch',  Icon: Package },
  { key: 'en_route',  label: 'En Route',  Icon: Truck },
  { key: 'done',      label: 'Done',      Icon: CheckCheck },
];

const STAGE_LABELS = {
  all: 'All Orders',
  accepted: 'Accepted',
  in_design: 'In Design',
  dispatch: 'Dispatch',
  en_route: 'En Route',
  done: 'Done',
};

const formatDate = (dateStr) => {
  try {
    return format(parseISO(dateStr), 'yyyy-MM-dd');
  } catch {
    return dateStr;
  }
};

const formatWeekday = (dateStr) => {
  try {
    return format(parseISO(dateStr), 'EEEE');
  } catch {
    return '—';
  }
};

const sortByOrderNumber = (orders) =>
  [...orders].sort((a, b) => (a.internal_order_number || '').localeCompare(b.internal_order_number || ''));

// Membership is selected server-side before limiting. Counts are separate live
// reads; neither their values nor local overlays establish list completeness.
const fetchOrdersForStage = async (date, stageKey) => {
  const page = await getPipelineOrders({ date, stage: stageKey });
  const queryCount = stageKey === 'dispatch' ? 2 : 1;
  const orders = page?.orders;
  if (!page || typeof page.has_more !== 'boolean' || page.limit_per_query !== 1000
    || page.query_count !== queryCount || !Array.isArray(orders)
    || orders.length > 1000 * queryCount || (page.has_more && orders.length < 1000)
    || !orders.every(order => order && typeof order === 'object'
      && typeof order.id === 'string' && order.id.trim().length > 0
      && (order.internal_order_number == null || typeof order.internal_order_number === 'string'))
    || new Set(orders.map(order => order.id)).size !== orders.length) {
    throw new Error('Order list unavailable');
  }
  return { ...page, orders: sortByOrderNumber(orders) };
};

const PipelineRow = ({ day, loading, isToday, expandedStageKey, onToggleExpand, orders, hasMore, ordersLoading, ordersError, onRetry }) => {
  const stages = day?.stages ?? {};
  const total = day?.total ?? 0;
  const dateLabel = day?.date ? formatDate(day.date) : '—';
  const dayLabel = isToday ? 'Today' : (day?.date ? formatWeekday(day.date) : '—');

  return (
    <div data-testid={`pipeline-row-wrapper-${day?.date ?? 'pending'}`}>
      <div
        className={`grid grid-cols-[18rem_repeat(5,minmax(0,1fr))] divide-x divide-stone-200 ${isToday ? 'bg-stone-50' : ''}`}
        data-testid={`pipeline-row-${day?.date ?? 'pending'}`}
      >
        {/* Left: date + total. The whole cell is the control, matching the
            stage cells — previously only the number itself was clickable, which
            made an apparently identical box behave differently. */}
        <button
          type="button"
          onClick={() => onToggleExpand(day?.date, 'all')}
          disabled={!day?.date}
          className={`flex flex-col justify-center items-start px-6 py-5 min-w-0 text-left transition-colors hover:bg-stone-100 ${
            expandedStageKey === 'all' ? 'bg-primary/5' : ''
          }`}
          data-testid={`pipeline-day-${day?.date ?? 'pending'}`}
        >
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-stone-400 mb-1">
            {dayLabel}
          </p>
          <p className="text-sm text-stone-500 mb-3">{dateLabel}</p>

          {/* Count and value sit together: they describe the same day, and the
              money is the second thing you want when glancing at a row.

              They can legitimately disagree. The five stage buckets cover every
              status except quarantined `new`, so a quarantined order adds to the
              value while not being counted. Summed server-side in whole cents;
              see build_pipeline_report. */}
          <div className="flex w-full items-end gap-6">
            <div className="flex flex-col items-start">
              <span
                className={`text-4xl font-bold leading-none transition-opacity ${
                  expandedStageKey === 'all' ? 'text-primary' : 'text-stone-900'
                } ${loading ? 'opacity-30' : 'opacity-100'}`}
                data-testid={`pipeline-total-${day?.date ?? 'pending'}`}
              >
                {total}
              </span>
              <p className="text-xs text-stone-400 mt-1">orders</p>
            </div>
            <div className="ml-auto flex flex-col items-end text-right">
              {/* Dark olive green, deliberately not the design system's
                  `primary` forest green: that colour already means "this day is
                  expanded" on the count beside it, and reusing it here would
                  blunt that signal. */}
              <span
                className={`text-2xl font-bold leading-none text-[#556B2F] tabular-nums whitespace-nowrap transition-opacity ${
                  loading ? 'opacity-30' : 'opacity-100'
                }`}
                data-testid={`pipeline-value-${day?.date ?? 'pending'}`}
              >
                {formatCurrency((day?.value_cents ?? 0) / 100)}
              </span>
              <p className="text-xs text-stone-400 mt-1">Total Value</p>
            </div>
          </div>
        </button>

        {/* Stages — clickable on today's row only */}
        {STAGES.map(({ key, label, Icon }) => {
          const isActive = expandedStageKey === key;
          const content = (
            <>
              <Icon className={`h-5 w-5 ${isActive ? 'text-primary' : 'text-stone-400'}`} strokeWidth={1.5} />
              <p className={`text-[10px] uppercase tracking-[0.15em] font-semibold ${isActive ? 'text-primary' : 'text-stone-400'}`}>
                {label}
              </p>
              <p className={`text-3xl font-bold text-stone-900 transition-opacity ${loading ? 'opacity-30' : 'opacity-100'}`}>
                {stages[key] ?? 0}
              </p>
            </>
          );

          if (!isToday) {
            return (
              <div
                key={key}
                className="flex flex-col justify-center items-center flex-1 px-4 py-5 gap-2"
                data-testid={`pipeline-stage-${key}-${day?.date ?? 'pending'}`}
              >
                {content}
              </div>
            );
          }

          return (
            <button
              key={key}
              type="button"
              onClick={() => onToggleExpand(day?.date, key)}
              className={`flex flex-col justify-center items-center flex-1 px-4 py-5 gap-2 hover:bg-stone-100 transition-colors ${
                isActive ? 'bg-primary/5' : ''
              }`}
              data-testid={`pipeline-stage-${key}-${day?.date ?? 'pending'}`}
            >
              {content}
            </button>
          );
        })}
      </div>

      {expandedStageKey && (
        <div className="border-t border-stone-200 bg-stone-50/50 p-3" data-testid={`pipeline-orders-${day?.date ?? 'pending'}`}>
          <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">
            {STAGE_LABELS[expandedStageKey] || 'Orders'}
          </p>
          {ordersLoading ? (
            <p className="text-sm text-stone-400 text-center py-4">Loading orders…</p>
          ) : ordersError ? (
            <p role="alert">Unavailable — <button type="button" onClick={onRetry}>Try again</button></p>
          ) : !orders || orders.length === 0 ? (
            <p className="text-sm text-stone-400 text-center py-4">No orders in this bucket</p>
          ) : (
            <div className="space-y-1.5">
              {hasMore && (
                <p role="status" data-testid={`pipeline-orders-limit-${day.date}`} className="text-sm text-stone-600 mb-2">
                  {expandedStageKey === 'dispatch'
                    ? 'Limited to the first 1,000 designed and 1,000 failed orders. Additional matches are not shown.'
                    : 'Showing the first 1,000 matching orders. Additional matches are not shown.'}
                </p>
              )}
              {orders.map((order) => (
                <PipelineOrderRow key={order.id} order={order} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PipelineRows = ({ days }) => {
  const revision = useSyncExternalStore(
    subscribeActiveSessionRevision, getActiveSessionRevision, getActiveSessionRevision,
  );
  const scope = useMemo(() => ({ days, revision, active: true, reads: new Map() }), [days, revision]);
  const current = useRef(scope);
  current.current = scope;
  const [state, setState] = useState(null);
  const visible = state?.scope === scope ? state : { expanded: {}, results: {} };
  useEffect(() => {
    scope.active = true;
    return () => { scope.active = false; scope.reads.clear(); };
  }, [scope]);
  const owns = () => scope.active && current.current === scope
    && revision === getActiveSessionRevision();
  const publish = update => {
    if (!owns()) return;
    setState(previous => {
      if (!owns()) return previous;
      const base = previous?.scope === scope ? previous : { scope, expanded: {}, results: {} };
      return update(base);
    });
  };
  const readOrders = async (date, stageKey, retry = false) => {
    const key = `${date}::${stageKey}`;
    const prior = scope.reads.get(key);
    if (!owns() || prior?.status === 'loading' || (!retry && prior)) return;
    const attempt = { status: 'loading' };
    scope.reads.set(key, attempt);
    publish(previous => ({ ...previous, results: { ...previous.results, [key]: attempt } }));
    try {
      const page = await fetchOrdersForStage(date, stageKey);
      if (!owns() || scope.reads.get(key) !== attempt) return;
      attempt.status = 'success';
      publish(previous => ({ ...previous, results: { ...previous.results, [key]: { ...page, status: 'success' } } }));
    } catch {
      if (!owns() || scope.reads.get(key) !== attempt) return;
      attempt.status = 'unavailable';
      publish(previous => ({ ...previous, results: { ...previous.results, [key]: { status: 'unavailable' } } }));
    }
  };

  const handleToggleExpand = async (date, stageKey) => {
    if (!date) return;
    const currentlyActive = visible.expanded[date];

    if (currentlyActive === stageKey) {
      publish((prev) => {
        const next = { ...prev.expanded };
        delete next[date];
        return { ...prev, expanded: next };
      });
      return;
    }

    publish((prev) => ({ ...prev, expanded: { ...prev.expanded, [date]: stageKey } }));
    await readOrders(date, stageKey);
  };

  return (
    <Card data-testid="pipeline-card">
      <CardContent className="p-0 overflow-x-auto">
        {/* A shared minimum width keeps the fixed summary and five equal stages
            aligned across days, with horizontal scrolling on narrow screens. */}
        <div className="min-w-[48rem] divide-y divide-stone-200">
          {days.map((day, index) => {
            const date = day?.date;
            const expandedStageKey = date ? visible.expanded[date] : undefined;
            const cacheKey = date && expandedStageKey ? `${date}::${expandedStageKey}` : null;
            const result = visible.results[cacheKey];
            return (
              <PipelineRow
                key={date ?? index}
                day={day}
                loading={false}
                isToday={index === 0}
                expandedStageKey={expandedStageKey}
                onToggleExpand={handleToggleExpand}
                orders={result?.orders}
                hasMore={result?.has_more}
                ordersLoading={result?.status === 'loading'}
                ordersError={result?.status === 'unavailable'}
                onRetry={() => readOrders(date, expandedStageKey, true)}
              />
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const PipelineCard = ({ days, loading, error, onRetry }) => {
  if (loading) return <Card data-testid="pipeline-card"><CardContent>Loading…</CardContent></Card>;
  if (error || !days?.length) return (
    <Card data-testid="pipeline-card"><CardContent>
      <p role="alert">Unavailable — <button type="button" onClick={onRetry}>Try again</button></p>
    </CardContent></Card>
  );
  return <PipelineRows days={days} />;
};

export default PipelineCard;
